import useJobStream from '../hooks/useJobStream';
import LiveJobStatus from '../components/LiveJobStatus';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ExecutionProviderBadge from '../components/ExecutionProviderBadge';
import bakingApi from '../services/bakingApi';

function StatusPill({ status }) {
  return <span className={`status-pill status-${status}`}>{status}</span>;
}

function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { revision: liveRevision, status: liveStatus } = useJobStream({});
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (liveRevision === 0) setLoading(true);
      setError(null);
      try {
        const data = await bakingApi.listJobs();
        if (!cancelled) setJobs(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load jobs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [liveRevision]);

  return (
    <div>
      <LiveJobStatus status={liveStatus} />
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="product-page-title">Jobs</h1>
          <p className="product-page-subtitle mb-0">
            Queued jobs start when a compatible worker is available.
          </p>
        </div>
        <Button as={Link} to="/console/jobs/new" variant="primary">
          New Bake
        </Button>
      </div>

      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading jobs…
        </div>
      )}

      {error && <div className="error-state">{error}</div>}

      {!loading && !error && jobs.length === 0 && (
        <div className="product-card empty-state">
          <p className="mb-3">No bake jobs yet.</p>
          <Button as={Link} to="/console/jobs/new" variant="outline-primary">
            Create your first bake job
          </Button>
        </div>
      )}

      {!loading && !error && jobs.length > 0 && (
        <div className="product-card p-0">
          <Table responsive hover className="product-table mb-0">
            <thead>
              <tr>
                <th>Status</th>
                <th>Provider</th>
                <th>Project</th>
                <th>Scene</th>
                <th>Unity</th>
                <th>Created</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id}>
                  <td>
                    <Link to={`/console/jobs/${job.id}`}>
                      <StatusPill status={job.status} />
                    </Link>
                  </td>
                  <td>
                    <ExecutionProviderBadge
                      provider={job.execution_provider || 'unity'}
                    />
                  </td>
                  <td>{job.project_name}</td>
                  <td className="mono">{job.scene_asset_path}</td>
                  <td>
                    {job.execution_provider === 'maya' ? '—' : job.unity_version}
                  </td>
                  <td>{new Date(job.created_at).toLocaleString()}</td>
                  <td>{job.status_reason}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {!loading && !error && (
        <Alert variant="light" className="mt-3 border">
          Remote Unity execution is not implemented in this release. Jobs remain queued until a
          worker claims them in a later slice.
        </Alert>
      )}
    </div>
  );
}

export default JobsPage;
